import { useEffect, useState } from "react";
import { Box, Button, Card, CardContent, CircularProgress, Stack, TextField, Typography, Divider } from "@mui/material";
import { bridgeStripeApi } from "./bridgeStripeApi";
import { normalizeTransfer } from "./bridgeStripeNormalize";

export default function BridgeStripeTransferPanel({ defaultMetadata }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [transfer, setTransfer] = useState(null);

  const legalId = typeof window !== 'undefined'
    ? (window.localStorage.getItem('agent-legal-id') || '').trim()
    : '';

  const [amount, setAmount] = useState(1);
  const [sourceAddress, setSourceAddress] = useState("");
  const [paymentRail, setPaymentRail] = useState("solana");
  const [sourceCurrency, setSourceCurrency] = useState("usdc");
  const [projectId, setProjectId] = useState(defaultMetadata?.projectId || "test id");

  const refresh = async () => {
    setErr(null);
    if (!legalId) {
      setErr({ ok: false, error: 'Missing legalId (agent-legal-id)' });
      return;
    }
    const res = await bridgeStripeApi.getTransfer(legalId, { method: paymentRail === 'pix' ? 'pix' : 'crypto' });
    if (!res.ok) {
      setErr(res);
      return;
    }
    setTransfer(normalizeTransfer(res.data));
  };

  useEffect(() => {
    refresh();
  }, []);

  const create = async () => {
    setBusy(true);
    setErr(null);
    if (!legalId) {
      setBusy(false);
      setErr({ ok: false, error: 'Missing legalId (agent-legal-id)' });
      return;
    }
    const res = await bridgeStripeApi.createTransfer({
      legalId,
      amount,
      sourceAddress,
      paymentRail,
      sourceCurrency,
      metadata: {
        ...(defaultMetadata || {}),
        projectId
      }
    });
    setBusy(false);

    if (!res.ok) {
      setErr(res);
      return;
    }

    setTransfer(normalizeTransfer(res.data));
  };

  const canCreate = amount > 0 && sourceAddress?.trim()?.length > 10;

  return (
    <Card sx={{ borderRadius: 3 }}>
      <CardContent>
        <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
          Stablecoin transfer test
        </Typography>

        {err ? (
          <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, bgcolor: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)" }}>
            <Typography sx={{ fontWeight: 700 }}>Error</Typography>
            <Typography variant="body2">{String(err.error || "Unknown error")}</Typography>
            <pre style={{ marginTop: 10, whiteSpace: "pre-wrap" }}>{JSON.stringify(err.data, null, 2)}</pre>
          </Box>
        ) : null}

        <Stack spacing={1.25} sx={{ maxWidth: 720 }}>
          <TextField
            label="Amount"
            type="number"
            value={amount}
            onChange={(e) => setAmount(Number(e.target.value))}
            size="small"
            inputProps={{ min: 0 }}
          />
          <TextField
            label="Source address (customer wallet)"
            value={sourceAddress}
            onChange={(e) => setSourceAddress(e.target.value)}
            size="small"
            placeholder="AnwdkBw..."
          />
          <Stack direction="row" spacing={1}>
            <TextField label="Payment rail" value={paymentRail} onChange={(e) => setPaymentRail(e.target.value)} size="small" sx={{ flex: 1 }} />
            <TextField label="Currency" value={sourceCurrency} onChange={(e) => setSourceCurrency(e.target.value)} size="small" sx={{ flex: 1 }} />
          </Stack>
          <TextField
            label="Metadata.projectId"
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            size="small"
          />

          <Stack direction="row" spacing={1}>
            <Button variant="contained" disabled={busy || !canCreate} onClick={create}>
              {busy ? <CircularProgress size={16} /> : null}
              Create transfer
            </Button>
            <Button variant="outlined" onClick={refresh} disabled={busy}>
              Refresh status
            </Button>
          </Stack>
        </Stack>

        {transfer ? (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2">
              Transfer: <b>{transfer.id}</b>
            </Typography>
            <Typography variant="body2">
              State: <b>{transfer.state}</b>
            </Typography>
            {transfer.deposit?.ToAddress ? (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Deposit to: <b>{transfer.deposit.ToAddress}</b>
              </Typography>
            ) : null}
            <Box sx={{ mt: 1.5, bgcolor: "#0B1220", color: "#E5E7EB", borderRadius: 2, p: 1.5 }}>
              <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>{JSON.stringify(transfer.raw, null, 2)}</pre>
            </Box>
          </>
        ) : (
          <Typography variant="body2" sx={{ mt: 2, color: "#6B7280" }}>
            No transfer yet.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
